/**
 * espaces.meta.js — source UNIQUE des espaces de connexion TyCopro.
 *
 * Deux applications distinctes, deux portes distinctes :
 *   - « app »     → https://app.tycopro.fr     syndics bénévoles, conseil syndical, copropriétaires ;
 *   - « manager » → https://manager.tycopro.fr opérateurs de l'ANAH et collectivités (TyCopro Manager).
 *
 * Importé à la fois :
 *   - par les composants React (import) : barre de navigation, pied de page, /connexion ;
 *   - par les scripts Node `build-content.js`, `prerender-seo.js` et `check-prerender.js`
 *     (require) : liens du contenu MDX, corps pré-rendu de /connexion, contrôle du build.
 * Le texte pré-rendu (vu sans JavaScript) et le rendu React ne peuvent donc pas diverger.
 *
 * CommonJS (`module.exports`) volontaire, comme tools.meta.js : interop `require`
 * (scripts Node) + `import` (CRA/Babel). Ne pas convertir en ESM.
 *
 * ⛔ SÉCURITÉ — une adresse de connexion ne se recopie jamais telle quelle depuis
 * l'environnement. Les variables REACT_APP_APP_URL / REACT_APP_MANAGER_URL sont des
 * arguments de build : une valeur fausse (adresse de développement, faute de frappe,
 * domaine tiers) enverrait chaque visiteur saisir son mot de passe ailleurs, sans
 * aucune erreur visible. `origineSure` n'accepte donc qu'une ORIGINE https d'un hôte
 * tycopro.fr, sans identifiants, port, chemin ni paramètre ; tout le reste retombe
 * sur l'adresse de production, avec un avertissement. En développement seulement,
 * http://localhost et http://127.0.0.1 sont admis.
 */

const ORIGINES_PAR_DEFAUT = Object.freeze({
    app: "https://app.tycopro.fr",
    manager: "https://manager.tycopro.fr",
});

const DOMAINE_AUTORISE = "tycopro.fr";
const HOTES_LOCAUX = ["localhost", "127.0.0.1"];

/**
 * Renvoie l'origine `valeur` si elle est sûre, sinon `repli`.
 * Comparaison sur l'URL ANALYSÉE, jamais sur le texte : « https://app.tycopro.fr@evil.test »
 * commence bien par l'origine attendue, mais son hôte réel est evil.test.
 */
function origineSure(valeur, repli, options) {
    const autoriserLocal = Boolean(options && options.autoriserLocal);
    if (valeur === undefined || valeur === null || String(valeur).trim() === "") return repli;

    const brut = String(valeur).trim().replace(/\/+$/, "");
    let url;
    try {
        url = new URL(brut);
    } catch (e) {
        return refuser(brut, repli, "adresse illisible");
    }

    if (url.username || url.password) return refuser(brut, repli, "identifiants dans l'adresse");
    if (url.search || url.hash) return refuser(brut, repli, "paramètres dans l'adresse");
    if (url.pathname && url.pathname !== "/") return refuser(brut, repli, "chemin dans l'adresse");

    const hote = url.hostname.toLowerCase();
    const local = HOTES_LOCAUX.includes(hote);

    if (local) {
        if (!autoriserLocal) return refuser(brut, repli, "adresse locale hors développement");
        if (url.protocol !== "http:" && url.protocol !== "https:") return refuser(brut, repli, "protocole");
        return `${url.protocol}//${url.host}`;
    }

    if (url.protocol !== "https:") return refuser(brut, repli, "https obligatoire");
    if (url.port) return refuser(brut, repli, "port explicite");
    const domaineTycopro = hote === DOMAINE_AUTORISE || hote.endsWith(`.${DOMAINE_AUTORISE}`);
    if (!domaineTycopro) return refuser(brut, repli, `hôte hors ${DOMAINE_AUTORISE}`);

    return `https://${hote}`;
}

function refuser(valeur, repli, motif) {
    // Bruyant à dessein : un repli silencieux se rediagnostique des mois plus tard.
    if (typeof console !== "undefined" && console.warn) {
        console.warn(`[espaces] adresse refusée (${motif}) : « ${valeur} » → ${repli}`);
    }
    return repli;
}

// ⚠️ Écrire `process.env.REACT_APP_…` EN ENTIER : CRA remplace ces expressions au
// build (DefinePlugin), mais `process` seul n'existe pas dans le navigateur — un
// `typeof process` y vaudrait "undefined" et ignorerait toute configuration. Les
// scripts Node lisent, eux, le vrai environnement.
// Adresse locale admise seulement en développement ou en test, jamais quand NODE_ENV
// est absent (scripts de build) ni en production.
const AUTORISER_LOCAL = process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";

const ORIGINE_APP = origineSure(process.env.REACT_APP_APP_URL, ORIGINES_PAR_DEFAUT.app, {
    autoriserLocal: AUTORISER_LOCAL,
});
let ORIGINE_MANAGER = origineSure(process.env.REACT_APP_MANAGER_URL, ORIGINES_PAR_DEFAUT.manager, {
    autoriserLocal: AUTORISER_LOCAL,
});
// Deux espaces = deux origines : une configuration qui les confondrait enverrait les
// opérateurs dans l'application des syndics (ou l'inverse).
if (ORIGINE_MANAGER === ORIGINE_APP) {
    ORIGINE_MANAGER = refuser(ORIGINE_MANAGER, ORIGINES_PAR_DEFAUT.manager, "même origine que l'app");
}

const hoteDe = (origine) => origine.replace(/^https?:\/\//, "");

const ESPACES = Object.freeze([
    Object.freeze({
        id: "app",
        nom: "TyCopro",
        titre: "Espace syndic et copropriétaires",
        // Une phrase, sans jargon : c'est elle qui fait choisir la bonne porte.
        pourQui: "Syndic bénévole, membre du conseil syndical ou copropriétaire.",
        detail:
            "Gérez votre copropriété, préparez l'assemblée générale, consultez vos documents et vos charges.",
        // Qui se connecte ici : la liste que lit un visiteur qui hésite entre les deux portes.
        publics: ["Syndic bénévole", "Membre du conseil syndical", "Copropriétaire"],
        origine: ORIGINE_APP,
        hote: hoteDe(ORIGINE_APP),
        connexion: `${ORIGINE_APP}/login`,
        inscription: `${ORIGINE_APP}/signup`,
        noteInscription: "Pas encore de compte ? Créez-le en ligne en quelques minutes.",
    }),
    Object.freeze({
        id: "manager",
        nom: "TyCopro Manager",
        titre: "Espace opérateurs et collectivités",
        pourQui: "Équipes des opérateurs de l'ANAH et des collectivités qui accompagnent des copropriétés.",
        detail:
            "Suivez les copropriétés accompagnées dans vos opérations (OPAH, POPAC ou en diffus).",
        publics: [
            "Chargé·e de mission d'un opérateur",
            "Responsable d'agence",
            "Direction de groupe",
            "Collectivité",
        ],
        origine: ORIGINE_MANAGER,
        hote: hoteDe(ORIGINE_MANAGER),
        connexion: `${ORIGINE_MANAGER}/login`,
        // ⛔ Pas d'inscription en libre-service : les comptes sont créés par TyCopro,
        // puis par le responsable de chaque agence.
        inscription: null,
        noteInscription: "Pas d'inscription en ligne : les accès sont ouverts par TyCopro pour votre agence.",
        page: "/operateurs-et-collectivites",
    }),
]);

function espace(id) {
    const e = ESPACES.find((x) => x.id === id);
    if (!e) throw new Error(`[espaces] espace inconnu : ${id}`);
    return e;
}

// Page du site qui présente les deux portes (repli sans JavaScript, lien à partager).
const PAGE_CONNEXION = "/connexion";

// Contact (problème de connexion, ouverture d'un accès Manager). Même adresse que
// lib/legalInfo.js — recopiée ici parce que ce module doit rester lisible par Node.
const CONTACT_EMAIL = "contact@tycopro.fr";
const mailto = (sujet) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(sujet)}`;
const MAILTO_MANAGER = mailto("TyCopro Manager — demande d'information");
const MAILTO_CONNEXION = mailto("Problème de connexion");

module.exports = {
    ESPACES,
    espace,
    origineSure,
    ORIGINES_PAR_DEFAUT,
    PAGE_CONNEXION,
    CONTACT_EMAIL,
    MAILTO_MANAGER,
    MAILTO_CONNEXION,
};
